import React, { useState, useRef, useEffect, useCallback } from "react";
import { styled } from "@linaria/react";
import { css } from "@linaria/core";
import { useTheme, ITheme } from "src/theme";
import Input from "@components/Input";
import Icon from "@components/Icon";
import { InputGroup } from "@components/_Revamp/InputGroup";
import { EverGreenStyle } from "@utils/Style/everGreen";
import DOMPurify from "dompurify";

const Container = styled.div<StyledProps>`
  display: block;
  position: relative;
  margin: ${(props) =>
    typeof props.margin === "number"
      ? `${props.margin || 0}px`
      : props.margin || 0};
  cursor: pointer;
  border-radius: 4px;
`;

const IconPosition = css`
  position: absolute;
  top: 50%;
  right: 20px;
  @media screen and (min-width: 768px) {
    right: 25px;
  }

  transform: translateY(-50%);
`;
const IconRotate = css`
  transform: translateY(-50%) rotate(180deg);
`;

const DropdownListContainer = styled.div<StyledProps>`
  position: absolute;
  background: ${(props) => props.theme?.colors.grey[200]};
  width: 100%;
  z-index: 10;
  box-shadow: 0 2px 2px 0 rgba(204, 204, 204, 0.5);
  border-radius: 4px;
  transform: translateY(${(prop) => (prop.variant === "default" ? 8 : -2)}px);
`;

const DropwdownHeader = styled.div<StyledProps>`
  height: 48px;
  width: 100%;
  border: 1px solid;
  display: block;
  position: relative;
  box-sizing: border-box;
  background: ${(props) => props.theme?.colors.grey[200]};
  border: 0;
  border-bottom: ${(props) => "1px solid " + props.theme?.colors.grey[100]};
  background: transparent;
  z-index: 1;
  cursor: pointer;
`;

const DropwdownHeaderInput = styled.div<StyledProps>`
  border-radius: 4px;
  position: relative;
  z-index: ${(prop) => (prop.isOpen ? 11 : "unset")};
`;

const SelectedText = styled.p<StyledProps>`
  text-align: start;
  margin: 0;
  padding: 20px 0;
  color: ${(props) => props.theme?.colors.grey[500]};
  font-family: ${(props) => props.theme?.fontFamily + ""};
  border-bottom: ${(props) =>
    props.isOpen ? "1px solid " + props.theme?.colors.grey[100] : "none"};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  font-size: 14px;
  font-weight: 600;
`;

const PlaceHolderText = styled.p<StyledProps>`
  text-align: start;
  margin: 0;
  padding: 20px 0;
  color: #c9c9c9;
  font-family: ${(props) => props.theme?.fontFamily + ""};
  border-bottom: ${(props) => (props.isOpen ? "1px solid #c4c4c4" : "none")};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  font-size: 14px;
  opacity: 0.5;
  font-weight: 500;
`;

const DropdownList = styled.ul<StyledProps>`
  list-style: none;
  padding: 0;
  padding-bottom: 15px;
  background: ${(props) => props.theme?.colors.grey[200]};
  margin: 0;
  box-sizing: border-box;
  border: 1px solid;
  border-color: ${(props) =>
    props.isOpen && props.variant === "default"
      ? "#c4c4c4"
      : props.theme?.colors.grey[100]};
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 19px;
    padding: 3px;
  }
  &::-webkit-scrollbar-track {
    background-color: ${(props) => props.theme?.colors.grey[700]};
    border-radius: 0 0 3px 0;
  }
  &::-webkit-scrollbar-thumb {
    height: 120px;
    border-radius: 10px;
    border: 3px solid ${(props) => props.theme?.colors.grey[700]};
    background-color: ${(props) =>
      props.theme?.colors.secondary || props.theme?.colors.grey[700]};
  }
`;

const ListItem = styled.i<StyledProps>`
  display: block;
  text-align: start;
  padding: 15px 19px;
  font-size: ${(props) => props.theme?.fontSize.text + "px"};
  font-family: ${(props) => props.theme?.fontFamily + ""};
  color: ${(props) => props.theme?.colors.grey[500]};
  font-style: normal;
  &:hover {
    background: ${(props) => props.theme?.colors.grey[600]};
  }
`;

const ClearButton = styled.button<StyledProps>`
  width: 25px;
  height: 25px;
  border: none;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: 12px;
  z-index: 1;
  cursor: pointer;
  background-color: ${(props) => props.theme?.colors.grey[600]};
  .icon-cancel {
    width: 9px;
    height: 9px;
  }
  &[class^="clear-btn-2"] {
    top: unset;
    bottom: -10%;
  }
`;

export interface DropdownProps {
  label?: string;
  id?: string;
  value?: string | undefined;
  source?: any[];
  placeholder?: string;
  theme?: ITheme;
  onChange: (e: any) => void;
  onFocus?: (e: any) => void;
  onBlur?: (e: any) => void;
  bindLabel: string;
  bindId: string;
  margin: string | number;
  name?: string;
  variant: "default" | "auto-complete";
  filteredList?: Array<any> | null;
}

export interface StyledProps {
  theme?: ITheme;
  isOpen?: boolean;
  margin?: string | number;
  variant?: string;
}

function Dropdown({
  label = "",
  id = "",
  value,
  source = [],
  placeholder,
  onChange,
  bindId,
  bindLabel,
  margin,
  name,
  variant,
  onFocus,
  onBlur,
  filteredList = null,
}: DropdownProps) {
  const initValue: any | undefined = source.find(
    (o: any) => o[bindId] === value
  );
  const [selected, change] = useState<any | null | undefined>(initValue);
  const [searchText, setSearchText] = useState<string | undefined>(
    initValue ? initValue[bindLabel] : undefined
  );
  const [suggestions, setSuggestions] = useState<any>(filteredList || source);
  const [isSearching, setIsSearchig] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const theme: ITheme = useTheme();
  const refContainer = useRef<HTMLDivElement>(null);

  const toggling = () => {
    if (!isOpen) {
      setSuggestions(filterSuggestion(undefined));
    } else {
      setIsSearchig(false);
    }
    setIsOpen(!isOpen);
  };
  const onSelected = (event: any, source: any) => {
    change(source);
    if (onChange) {
      onChange({
        ...event,
        target: { ...event.target, name, value: source[bindId] },
      });
    }
    setSearchText(source ? source[bindLabel] : undefined);
    toggling();
  };

  const handleFocus = (e: any) => {
    if (!isOpen) {
      setSuggestions(filteredList || source);
    }
    if (onFocus) {
      onFocus(e);
    }
    setIsOpen(true);
  };

  const onChangeSearch = (event: any) => {
    if (!isSearching) {
      setIsSearchig(true);
    }
    const text = event.target.value;
    setSearchText(text);
    setSuggestions(filterSuggestion(text));
    console.log("onChangeSearch", { text });
  };

  const handleClickOutside = useCallback(
    (event: any) => {
      if (refContainer.current && refContainer.current.contains(event.target)) {
        return;
      }
      if (onBlur && isOpen) {
        onBlur(selected ? selected[bindLabel] : undefined);
      }
      setSearchText(selected ? selected[bindLabel] : undefined);
      setIsOpen(false);
    },
    [bindLabel, selected, isOpen, onBlur]
  );

  const onClear = (event: any) => {
    event.stopPropagation();
    setSearchText("");
    setSuggestions(filterSuggestion(""));
  };

  const filterSuggestion = (text: string | undefined) => {
    let selectedSource = filteredList || source;
    if (!text || text === "") {
      return selectedSource;
    }
    return selectedSource.filter((item) => {
      const lable: string = item[bindLabel];
      return lable.toLowerCase().includes(text.toLowerCase());
    });
  };

  const highlight = (origin: string, highlightText: string): string => {
    if (highlightText === "") {
      return origin;
    }
    let modifiedString = "";
    let i = 0;
    const searchTextLength = highlightText.length;
    while (i < origin.length) {
      const refer = origin.substring(i, i + searchTextLength);
      if (refer.toLowerCase() === highlightText.toLowerCase()) {
        modifiedString += `<b>${refer}</b>`;
        i = i + searchTextLength;
      } else {
        modifiedString += origin.substring(i, i + 1);
        i++;
      }
    }
    return modifiedString;
  };

  useEffect(() => {
    if (refContainer.current) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [selected, handleClickOutside]);

  return (
    <Container
      theme={theme}
      isOpen={isOpen}
      margin={margin}
      ref={refContainer}
      variant={variant}
    >
      {variant === "default" ? (
        <DropwdownHeader
          theme={theme}
          onClick={toggling}
          onFocus={onFocus}
          onBlur={onBlur}
          isOpen={isOpen}
        >
          {!selected ? (
            <PlaceHolderText theme={theme} isOpen={isOpen}>
              {placeholder}{" "}
            </PlaceHolderText>
          ) : null}
          {selected ? (
            <SelectedText theme={theme} isOpen={isOpen}>
              {" "}
              {selected[bindLabel]}{" "}
            </SelectedText>
          ) : null}
          <Icon
            size="xsm"
            className={isOpen ? `${IconRotate} ${IconPosition}` : IconPosition}
            name={isOpen ? "chevron-down-grey" : "chevron-down-grey"}
          />
        </DropwdownHeader>
      ) : (
        <DropwdownHeaderInput isOpen={isOpen}>
          <InputGroup
            label={label}
            id={id}
            placeholder={placeholder}
            inputType="input"
            inputProps={{
              textAlign: "left",
              value: searchText || "",
              autoComplete: "off",
            }}
            onFocus={handleFocus}
            theme={EverGreenStyle}
            // dropdownProps={{
            //   value: occupation.natureOfBusiness.id,
            //   source: natureOfBusinessList,
            // }}
            onChange={onChangeSearch}
            // onChange={(e) => {
            //   trackOnFormStartOrSuccess("DS_occupation_form");

            //   dispatch(
            //     updateNameOfBusiness(
            //       mappedNatureOfBusinessList.current.get(e.target.value)
            //     )
            //   );
            // }}
            onBlurShowError={true}
          />
          {isOpen && searchText && (
            <ClearButton
              theme={theme}
              className={"clear-btn-2"}
              onClick={onClear}
            >
              <Icon name="close-grey" size="sm" className="icon-cancel" />
            </ClearButton>
          )}
        </DropwdownHeaderInput>
      )}
      {isOpen && (
        <DropdownListContainer theme={theme} variant={variant}>
          <DropdownList theme={theme} isOpen={isOpen} variant={variant}>
            {suggestions.map((_item: any, index: number) => (
              <ListItem
                onClick={(e) => onSelected(e, _item)}
                key={`${index}`}
                theme={theme}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(isSearching
                    ? highlight(_item[bindLabel], searchText || "")
                    : _item[bindLabel])
                }}
              />
            ))}
          </DropdownList>
        </DropdownListContainer>
      )}
    </Container>
  );
}
Dropdown.defaultProps = {
  value: undefined,
  source: [],
  placeholder: "",
  onChange: () => {},
  bindLabel: "label",
  bindId: "id",
  margin: "0 0 30px",
  variant: "default",
};

export default Dropdown;
